'use client'

// =============================================================================
// components/ui/TopBar.tsx — Barre supérieure partagée (combinée)
// =============================================================================

import { useState, useEffect, useRef } from 'react'
import { useSession, signIn } from 'next-auth/react'
import Link from 'next/link'
import WeatherWidget from '@/components/WeatherWidget'
import { useOnlinePresence } from '@/hooks/landing/useOnlinePresence'
import { useOrgBrand, orgInitials, type OrgBrand } from '@/hooks/useOrgBrand'
import type { OnlineInfo } from '@/types/landing'
import { CommandPalette } from './CommandPalette'
import { ThemeToggle } from './ThemeToggle'

// ── Types ─────────────────────────────────────────────────────────────────────

interface AiArticle {
  id: string
  titre: string
  url: string
  urlImage?: string | null
  source: {
    nom: string
    logoUrl?: string | null
    url?: string
    language?: string
  }
}

interface AiNews {
  region: string
  summary: string
  articles: AiArticle[]
  fromCache: boolean
  timestamp: string
}

// ── Props combinées (issues des deux versions) ──────────────────────────────

interface TopBarProps {
  // provenant de la 1ʳᵉ version
  region?: string
  onRegionChange?: (code: string) => void
  news?: AiNews | null
  newsLoading?: boolean
  userLanguage?: string

  // provenant de la 2ᵉ version
  onToggleRight?: () => void
  rightOpen?: boolean
  onToggleSidebar?: () => void
  showMenu?: boolean
}

// ── Régions du Québec (liste complète, ordre de la 1ʳᵉ version) ────────────

const REGIONS = [
  { code: '01', nom: 'Bas-Saint-Laurent' },
  { code: '02', nom: 'Saguenay–Lac-Saint-Jean' },
  { code: '03', nom: 'Capitale-Nationale' },
  { code: '04', nom: 'Mauricie' },
  { code: '05', nom: 'Estrie' },
  { code: '06', nom: 'Montréal' },
  { code: '07', nom: 'Outaouais' },
  { code: '08', nom: 'Abitibi-Témiscamingue' },
  { code: '09', nom: 'Côte-Nord' },
  { code: '10', nom: 'Nord-du-Québec' },
  { code: '11', nom: 'Gaspésie–Îles-de-la-Madeleine' },
  { code: '12', nom: 'Chaudière-Appalaches' },
  { code: '13', nom: 'Laval' },
  { code: '14', nom: 'Lanaudière' },
  { code: '15', nom: 'Laurentides' },
  { code: '16', nom: 'Montérégie' },
  { code: '17', nom: 'Centre-du-Québec' },
]

// ── Utilitaires ──────────────────────────────────────────────────────────────

function extractDomain(url?: string | null): string | null {
  if (!url) return null
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return null
  }
}

// ── News Ticker ──────────────────────────────────────────────────────────────

function TickerItem({ article: a }: { article: AiArticle }) {
  const domain = extractDomain(a.source.url ?? a.url)
  const faviconUrl = domain
    ? `https://www.google.com/s2/favicons?domain=${domain}&sz=16`
    : null

  return (
    <a
      href={a.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        textDecoration: 'none',
        color: 'var(--text-secondary, rgba(255,255,255,0.55))',
        fontSize: 11,
        fontFamily: 'var(--font-mono)',
        flexShrink: 0,
      }}
    >
      {faviconUrl && (
        <img
          src={faviconUrl}
          alt=""
          width={14}
          height={14}
          style={{ borderRadius: 2, flexShrink: 0, opacity: 0.8 }}
          onError={(e) => {
            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
          }}
        />
      )}
      {a.urlImage && (
        <img
          src={a.urlImage}
          alt=""
          width={36}
          height={24}
          style={{
            objectFit: 'cover',
            borderRadius: 3,
            flexShrink: 0,
            opacity: 0.85,
          }}
          onError={(e) => {
            ;(e.currentTarget as HTMLImageElement).style.display = 'none'
          }}
        />
      )}
      <span style={{ color: 'var(--accent)', fontSize: 9 }}>▶</span>
      <span
        style={{
          maxWidth: 260,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {a.titre}
      </span>
      <span style={{ opacity: 0.4, fontSize: 9 }}>{a.source.nom}</span>
    </a>
  )
}

function NewsTicker({ articles }: { articles: AiArticle[] }) {
  const items = articles.slice(0, 6)
  if (!items.length) return null

  return (
    <div
      style={{
        overflow: 'hidden',
        maskImage:
          'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 48,
          alignItems: 'center',
          animation: 'ticker 40s linear infinite',
          whiteSpace: 'nowrap',
        }}
      >
        {[...items, ...items].map((a, i) => (
          <TickerItem key={i} article={a} />
        ))}
      </div>
      <style>{`
        @keyframes ticker {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  )
}

// ── Statut utilisateur ──────────────────────────────────────────────────────

const REGION_NICKNAMES: Record<string, string> = {
  '01': 'Fous du Fleuve',
  '02': 'Bleuets',
  '03': 'Têtes de Sirop',
  '04': 'Shawinigans',
  '05': 'Kings du Ski',
  '06': 'Cône-Orangeois',
  '07': "Voisins d'Ottawa",
  '08': 'Mineurs de Génie',
  '09': 'Bouffeurs de Vent',
  '10': 'Explorateurs du Permafrost',
  '11': 'Venteux de la Péninsule',
  '12': 'Beaucerons',
  '13': 'Rois du Bungalow',
  '14': 'Chaloteux',
  '15': 'Nordiques du Trafic',
  '16': 'Gratteux de Rive-Sud',
  '17': 'Inventeurs de la Poutine',
}

function formatPresenceLabel(info: OnlineInfo, hasUser: boolean): string {
  if (info.count === 0) return ''
  if (info.count === 1 && hasUser) return 'Vous êtes seul ici… ou presque'
  const entries = Object.entries(info.regions).filter(([, n]) => n > 0)
  if (entries.length === 0)
    return info.count === 1 ? '1 visiteur actif' : `${info.count} personnes actives`
  const parts = entries.map(([code, n]) => {
    const nick = REGION_NICKNAMES[code]
    return nick ? `${n} ${nick}` : `${n} connecté${n > 1 ? 's' : ''}`
  })
  const anonCount = info.count - entries.reduce((s, [, n]) => s + n, 0)
  if (anonCount > 0) parts.push(`${anonCount} curieux`)
  return parts.length === 1
    ? parts[0]
    : parts.slice(0, -1).join(', ') + ' et ' + parts[parts.length - 1]
}

function UserStatus() {
  const { data: session } = useSession()
  const onlineInfo = useOnlinePresence()

  if (!session) return null

  const firstName = (session.user?.name ?? '').split(' ')[0] || null
  const role = ((session.user as any)?.role ?? 'USAGER').toUpperCase()
  const roleColor =
    role === 'ADMIN'
      ? 'var(--coral, #f97316)'
      : role === 'VENDEUR'
      ? '#fbbf24'
      : 'var(--accent, #4f7cff)'
  const roleIcon = role === 'ADMIN' ? '⬡' : role === 'VENDEUR' ? '◆' : '◉'
  const xp = 0,
    xpMax = 100,
    level = 1
  const presence = formatPresenceLabel(onlineInfo, !!firstName)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 3,
        flexShrink: 1,
        minWidth: 0,
        maxWidth: 260,
        paddingRight: 10,
        borderRight: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      <div
        title={presence || undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          minWidth: 0,
          fontSize: 9,
          fontFamily: 'var(--font-mono)',
          letterSpacing: '0.08em',
          whiteSpace: 'nowrap',
        }}
      >
        <span className="hud-dot" style={{ flexShrink: 0 }} />
        {firstName && (
          <span
            style={{
              color: 'var(--accent-cyan, #00d4ff)',
              textTransform: 'uppercase',
              flexShrink: 0,
            }}
          >
            Bonjour, {firstName}
          </span>
        )}
        {presence && (
          <span
            style={{
              color: 'rgba(255,255,255,0.28)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            · {presence}
          </span>
        )}
        <span style={{ fontSize: 10, color: roleColor, flexShrink: 0 }}>
          {roleIcon}
        </span>
        <span style={{ fontSize: 9, color: '#fff', fontWeight: 600, flexShrink: 0 }}>
          Niveau {level}
        </span>
        <span
          style={{
            fontSize: 8,
            color: roleColor,
            background: `color-mix(in srgb, ${roleColor} 12%, transparent)`,
            border: `1px solid color-mix(in srgb, ${roleColor} 28%, transparent)`,
            borderRadius: 5,
            padding: '1px 5px',
            letterSpacing: '0.08em',
            flexShrink: 0,
          }}
        >
          {role}
        </span>
      </div>
    </div>
  )
}

// ── Menu utilisateur ────────────────────────────────────────────────────────

function UserMenu() {
  const { data: session, status } = useSession()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const groups: string[] = (session?.user as any)?.groups ?? []
  const isAdmin = groups.some((g) =>
    ['admin', 'amiral_admins', 'authentik admins'].includes(g.toLowerCase().trim())
  )

  useEffect(() => {
    function away(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [])

  if (status === 'loading')
    return (
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
        }}
      />
    )

  if (!session)
    return (
      <Link
        href="/login"
        style={{
          padding: '5px 12px',
          background: 'var(--accent)',
          color: '#fff',
          borderRadius: 6,
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >
        Connexion
      </Link>
    )

  const name = session.user?.name ?? session.user?.email ?? '?'
  const initials = name.slice(0, 2).toUpperCase()
  const role = (session.user as any)?.role ?? 'USER'
  const email = session.user?.email ?? ''
  const roleColor =
    role === 'ADMIN' ? '#f97316' : role === 'VENDEUR' ? '#fbbf24' : '#34d399'

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={name}
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--accent), #818cf8)',
          color: '#fff',
          border: `2px solid ${open ? 'var(--accent)' : 'transparent'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          fontWeight: 800,
          cursor: 'pointer',
          boxShadow: open
            ? '0 0 16px rgba(79,124,255,0.5)'
            : '0 0 8px rgba(79,124,255,0.2)',
          transition: 'all 0.15s',
          flexShrink: 0,
        }}
      >
        {initials}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 240,
            zIndex: 999,
            background: '#0b0f1a',
            border: '1px solid rgba(79,124,255,0.2)',
            borderRadius: 12,
            boxShadow:
              '0 20px 60px rgba(0,0,0,0.8), 0 0 0 1px rgba(79,124,255,0.06)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '14px 16px 10px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--accent), #818cf8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 13,
                  fontWeight: 800,
                  color: '#fff',
                }}
              >
                {initials}
              </div>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    color: '#fff',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {name}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.3)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: 'var(--font-mono)',
                  }}
                >
                  {email}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: roleColor,
                      display: 'inline-block',
                      boxShadow: `0 0 6px ${roleColor}80`,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 9,
                      color: roleColor,
                      letterSpacing: '0.1em',
                    }}
                  >
                    {role}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: '6px 0' }}>
            <DropItem icon="◈" label="Mon profil" href="/profil" onClick={() => setOpen(false)} />
            <DropItem icon="⚙" label="Paramètres" href="/parametres" onClick={() => setOpen(false)} />
            <DropItem icon="◎" label="Préférences IA" href="/parametres" onClick={() => setOpen(false)} />
            <DropItem icon="⬡" label="API externes" href="/parametres" onClick={() => setOpen(false)} />
            <DropItem icon="◉" label="Clés & tokens" href="/parametres" onClick={() => setOpen(false)} />

            {isAdmin && (
              <>
                <div
                  style={{
                    height: 1,
                    background: 'rgba(255,255,255,0.05)',
                    margin: '4px 12px',
                  }}
                />
                <DropItem
                  icon="⛨"
                  label="Administration"
                  href="/admin"
                  onClick={() => setOpen(false)}
                  highlight
                />
                <DropItem
                  icon="◧"
                  label="IAM Control"
                  href="/admin/iam"
                  onClick={() => setOpen(false)}
                  highlight
                />
              </>
            )}

            {groups.some((g) =>
              ['admin', 'vendeur', 'amiral_vendeurs'].includes(g.toLowerCase())
            ) && (
              <DropItem
                icon="◇"
                label="Marketplace"
                href="/marketplace"
                onClick={() => setOpen(false)}
              />
            )}

            <div
              style={{
                height: 1,
                background: 'rgba(255,255,255,0.05)',
                margin: '4px 12px',
              }}
            />
            <button
              onClick={() => {
                setOpen(false)
                // prompt=login force Authentik à REDEMANDER l'authentification
                // même si sa session SSO est encore ouverte — seule façon
                // d'entrer avec un autre compte sans vider ses cookies.
                signIn('authentik', { callbackUrl: '/' }, { prompt: 'login' })
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '8px 16px',
                background: 'none',
                border: 'none',
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <span style={{ fontSize: 13, opacity: 0.7 }}>⇄</span>
              Changer de compte
            </button>

            <div
              style={{
                height: 1,
                background: 'rgba(255,255,255,0.05)',
                margin: '4px 12px',
              }}
            />
            <button
              onClick={() => {
                setOpen(false)
                // Déconnexion FÉDÉRÉE : signOut() de NextAuth n'efface que le
                // cookie d'Amiral Blanc — la session SSO d'Authentik survivrait et
                // reconnecterait le même usager sans mot de passe au login suivant.
                window.location.href = '/api/auth/federated-logout'
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '8px 16px',
                background: 'none',
                border: 'none',
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: '#ef4444',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'rgba(239,68,68,0.08)')
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = 'none')
              }
            >
              <span style={{ fontSize: 13, opacity: 0.7 }}>⏻</span>
              Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function DropItem({
  icon,
  label,
  href,
  onClick,
  highlight,
}: {
  icon: string
  label: string
  href: string
  onClick: () => void
  highlight?: boolean
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 16px',
        textDecoration: 'none',
        fontFamily: 'var(--font-mono)',
        fontSize: 12,
        color: highlight ? '#f97316' : 'rgba(255,255,255,0.6)',
        transition: 'all 0.1s',
      }}
      onMouseEnter={(e) => {
        ;(e.currentTarget as HTMLElement).style.background =
          'rgba(255,255,255,0.04)'
        ;(e.currentTarget as HTMLElement).style.color = '#fff'
      }}
      onMouseLeave={(e) => {
        ;(e.currentTarget as HTMLElement).style.background = 'none'
        ;(e.currentTarget as HTMLElement).style.color = highlight
          ? '#f97316'
          : 'rgba(255,255,255,0.6)'
      }}
    >
      <span
        style={{
          fontSize: 13,
          opacity: 0.65,
          width: 16,
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      {label}
    </Link>
  )
}

// ── Composant principal ─────────────────────────────────────────────────────

/** Zone de marque : le logo téléversé par l'organisation ; à défaut, le logo Amiral + les initiales de l'organisation. */
function BrandMark({ org }: { org: OrgBrand | null }) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  if (org?.logoSrc && org.logoSrc !== failedSrc) {
    return <img className="hx-brand-logo" src={org.logoSrc} alt={org.name} onError={() => setFailedSrc(org.logoSrc)} />
  }
  return (
    <>
      <img src="/logo.png" alt="Amiral Blanc" width={26} height={26} style={{ objectFit: 'contain', filter: 'drop-shadow(0 0 8px rgba(79,124,255,0.4))' }} />
      {org && <span className="hx-org-mark" title={org.name}>{orgInitials(org.name)}</span>}
    </>
  )
}

export default function TopBar({
  region = '06',
  onRegionChange,
  news,
  newsLoading = false,
  userLanguage = 'fr',
  onToggleRight,
  rightOpen = false,
  onToggleSidebar,
  showMenu = false,
}: TopBarProps) {
  const [time, setTime] = useState(new Date())
  const [paletteOpen, setPaletteOpen] = useState(false)
  const org = useOrgBrand()

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  // Ctrl+K / Cmd+K global — Slice 3 (voir docs/architecture/command-center-ui.md,
  // Topbar : "Command Bar"). Écouteur global (document), pas seulement quand
  // la Topbar a le focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(o => !o)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const raw = news?.articles ?? []
  const filtered = raw.filter((a) => (a.source.language ?? 'fr') === userLanguage)
  const articles = filtered.length > 0 ? filtered : raw

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        padding: '0 12px 0 14px',
        background: 'rgba(4,5,10,0.99)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        gap: 12,
        overflow: 'hidden',
      }}
    >
      {/* ── Hamburger (mobile) ── */}
      {showMenu && (
        <button
          onClick={onToggleSidebar}
          aria-label="Menu"
          style={{
            flexShrink: 0,
            width: 32,
            height: 30,
            borderRadius: 7,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.6)',
            fontSize: 15,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ☰
        </button>
      )}

      {/* ── Logo et marque ── */}
      <Link
        href="/"
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          textDecoration: 'none',
        }}
      >
        <BrandMark org={org} />
        <div
          style={{
            padding: '14px 14px 11px 0',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: '#fff',
              letterSpacing: '-0.03em',
              lineHeight: 1,
              fontFamily: 'var(--font-display, inherit)',
            }}
          >
            Amiral Blanc<span style={{ color: 'var(--accent, #5865f2)' }}>.</span>
          </div>
          <div
            style={{
              fontSize: 9,
              color: 'rgba(255,255,255,0.22)',
              fontFamily: 'var(--font-mono)',
              marginTop: 3,
              letterSpacing: '0.12em',
            }}
          >
            {org ? org.name : 'Think Big!'}
          </div>
        </div>
      </Link>

      {/* ── LIVE + Région ── */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: 3,
          flexShrink: 0,
          paddingRight: 10,
          borderRight: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#ef4444',
              display: 'inline-block',
              animation: 'pulse-dot 1.2s ease-in-out infinite',
              boxShadow: '0 0 6px #ef444490',
            }}
          />
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              color: '#ef4444',
              letterSpacing: '0.12em',
            }}
          >
            LIVE
          </span>
        </span>
        {onRegionChange ? (
          <select
            value={region}
            onChange={(e) => onRegionChange(e.target.value)}
            style={{
              background: 'none',
              border: 'none',
              padding: '0 2px',
              color: 'rgba(255,255,255,0.4)',
              fontSize: 9,
              fontFamily: 'var(--font-mono)',
              cursor: 'pointer',
              outline: 'none',
              maxWidth: 120,
              letterSpacing: '0.04em',
            }}
          >
            {REGIONS.map((r) => (
              <option key={r.code} value={r.code} style={{ background: '#0b0f1a' }}>
                {r.nom}
              </option>
            ))}
          </select>
        ) : (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              color: 'rgba(255,255,255,0.3)',
            }}
          >
            {REGIONS.find((r) => r.code === region)?.nom ?? 'Montréal'}
          </span>
        )}
      </div>

      {/* ── Fil d'actualités ── */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          padding: '0 8px',
        }}
      >
        {newsLoading ? (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'rgba(255,255,255,0.2)',
            }}
          >
            ⟳ Actualités…
          </span>
        ) : (
          <NewsTicker articles={articles} />
        )}
      </div>

      {/* ── Command Search (Ctrl+K) ── */}
      <button
        onClick={() => setPaletteOpen(true)}
        aria-label="Recherche (Ctrl+K)"
        style={{
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(79,124,255,0.1)',
          borderRadius: 8, padding: '4px 8px', cursor: 'pointer',
          color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-mono)', fontSize: 10,
        }}
      >
        <span>🔎</span>
        {!showMenu && <span style={{ opacity: 0.5, fontSize: 9 }}>Ctrl+K</span>}
      </button>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* ── Statut utilisateur (caché sur mobile) ── */}
      {!showMenu && <UserStatus />}

      {/* ── Météo + Heure (caché sur mobile) ── */}
      <div
        style={{
          display: showMenu ? 'none' : 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
          borderRight: '1px solid rgba(255,255,255,0.07)',
          paddingRight: 10,
          marginRight: 2,
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <WeatherWidget />
        </div>
        <div
          style={{ flexShrink: 0, fontFamily: 'var(--font-mono)' }}
          suppressHydrationWarning
        >
          <div
            suppressHydrationWarning
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.65)',
              fontWeight: 300,
              letterSpacing: '0.04em',
            }}
          >
            {time.toLocaleTimeString('fr-CA', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </div>
          <div
            suppressHydrationWarning
            style={{
              fontSize: 8,
              color: 'rgba(255,255,255,0.22)',
              marginTop: 1,
            }}
          >
            {time.toLocaleDateString('fr-CA', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
            })}
          </div>
        </div>
      </div>

      {/* ── IA Toggle + User Menu ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        {onToggleRight && (
          <button
            onClick={onToggleRight}
            title="Panneau IA"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '5px 10px',
              background: rightOpen
                ? 'rgba(129,140,248,0.14)'
                : 'rgba(255,255,255,0.04)',
              border: `1px solid ${
                rightOpen ? 'rgba(129,140,248,0.35)' : 'rgba(255,255,255,0.08)'
              }`,
              borderRadius: 6,
              color: rightOpen ? '#818cf8' : 'rgba(255,255,255,0.35)',
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.06em',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            IA ◧
          </button>
        )}

        <ThemeToggle />

        <UserMenu />
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.35; }
        }
      `}</style>
    </div>
  )
}
